import { useCallback, useEffect, useState } from "react"
import { Button } from "primereact/button"
import { Column } from "primereact/column"
import { DataTable, DataTableExpandedRows, DataTableValueArray } from "primereact/datatable"
import { ProgressSpinner } from "primereact/progressspinner"
import { getPreLiquidation, getPreLiquidationItems } from "../../servicesHooks/Honorarium/useHonorariumPreLiquidationService"
import {
    DeletePreLiqDetailResultDTO,
    HonorariumPreLiquidationDTO,
    HonorariumPreLiquidationItemDTO,
    HonorariumPreLiquidationItemStatusId,
    HonorariumPreLiquidationStatusId,
} from "../../dto/HonorariumPreLiquidationDTO"
import { parseApiError } from "../../services/apiError"
import { formatMoney } from "../../utils/money"
import ReadonlyField from "../../components/ReadonlyField/ReadonlyField"
import useNotify from "../../hooks/useNotify"
import { formatDate } from "../FinancerDistribution/financerDistributionFormat"
import PreLiquidationItemDetails from "./PreLiquidationItemDetails"
import PreLiquidationItemStatusTag from "./PreLiquidationItemStatusTag"
import PreLiquidationStatusTag from "./PreLiquidationStatusTag"

interface PreLiquidationDetailProps {
    preLiqId: number;
    onBack: () => void;
}

/**
 * Vista de detalle de una pre-liquidación (DS §3.7): cabecera de solo lectura y sus ítems.
 * Cada ítem se despliega y muestra sus detalles, que se pueden eliminar mientras el ítem esté pendiente.
 */
export default function PreLiquidationDetail({ preLiqId, onBack }: PreLiquidationDetailProps) {
    const notify = useNotify();

    const [preLiq, setPreLiq] = useState<HonorariumPreLiquidationDTO | null>(null);
    const [items, setItems] = useState<HonorariumPreLiquidationItemDTO[]>([]);
    const [loading, setLoading] = useState(true);
    const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows | DataTableValueArray>({});

    /** Trae la cabecera y los ítems. `silent`: recarga después de eliminar, sin tapar la tabla con el spinner. */
    const load = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const [preLiqData, itemsData] = await Promise.all([
                getPreLiquidation(preLiqId),
                getPreLiquidationItems(preLiqId),
            ]);
            setPreLiq(preLiqData);
            setItems(itemsData);
        } catch (error: unknown) {
            const apiError = parseApiError(error);
            if (apiError.status === 404) {
                // Ya no existe (por ejemplo, otra persona la eliminó): no hay nada que mostrar
                notify.error(apiError.message);
                onBack();
                return;
            }
            notify.error('No se pudo cargar la pre-liquidación. Intentá de nuevo.');
        } finally {
            setLoading(false);
        }
    }, [preLiqId, notify, onBack]);

    useEffect(() => { load(); }, [load]);

    function handleDetailDeleted(result: DeletePreLiqDetailResultDTO) {
        if (result.preLiquidationCancelled) {
            notify.success('Pre-liquidación eliminada · no le quedaban detalles');
            onBack();
            return;
        }
        notify.success('Detalle eliminado');
        // Los montos de la cabecera y de los ítems los recalcula el backend
        load(true);
    }

    const isActive = preLiq?.statusId === HonorariumPreLiquidationStatusId.ACTIVE;

    return (
        <div className="preliq-detail">
            <div>
                <Button text icon="pi pi-angle-double-left" label="Volver" className="md-back-link" onClick={onBack} />
            </div>
            <h1 className="md-page-title">Detalle de pre-liquidación</h1>
            {preLiq && (
                <p className="md-entity-subtitle">{preLiq.professionalName} · {preLiq.billingEntityName}</p>
            )}

            {loading && (
                <div className="md-loading" role="status">
                    <ProgressSpinner strokeWidth="4" />
                    <span>Cargando…</span>
                </div>
            )}

            {!loading && preLiq && (
                <>
                    <div className="md-readonly-grid preliq-detail__summary">
                        <ReadonlyField label="Fecha">{formatDate(preLiq.createdDatetime)}</ReadonlyField>
                        <ReadonlyField label="Estado">
                            <PreLiquidationStatusTag statusId={preLiq.statusId} status={preLiq.status} />
                        </ReadonlyField>
                        <ReadonlyField label="Ítems pendientes">{preLiq.pendingItemCount}</ReadonlyField>
                        <ReadonlyField label="Ítems liquidados">{preLiq.liquidatedItemCount}</ReadonlyField>
                        <ReadonlyField label="Honorario profesional">
                            <strong>{formatMoney(preLiq.totalProfessionalAmount)}</strong>
                        </ReadonlyField>
                    </div>

                    <h2 className="md-section-title preliq-detail__section-title">Ítems</h2>
                    <DataTable
                        value={items}
                        dataKey="honorariumPreLiquidationItem"
                        expandedRows={expandedRows}
                        onRowToggle={(e) => setExpandedRows(e.data)}
                        rowExpansionTemplate={(item: HonorariumPreLiquidationItemDTO) => (
                            <PreLiquidationItemDetails
                                item={item}
                                editable={isActive && item.statusId === HonorariumPreLiquidationItemStatusId.PENDING}
                                onDetailDeleted={handleDetailDeleted}
                            />
                        )}
                        className="crudTable"
                        emptyMessage="Sin ítems cargados."
                    >
                        <Column expander style={{ width: '48px' }} />
                        <Column field="sourceType" header="Tipo" />
                        <Column
                            header="Período"
                            body={(row: HonorariumPreLiquidationItemDTO) => `${formatDate(row.fromDate)} – ${formatDate(row.toDate)}`}
                        />
                        <Column field="detailCount" header="Registros" className="text-center" headerClassName="col-center" style={{ width: '100px' }} />
                        <Column
                            field="totalAmount"
                            header="Monto total"
                            body={(row: HonorariumPreLiquidationItemDTO) => formatMoney(row.totalAmount)}
                            className="text-right"
                            headerClassName="col-right"
                            style={{ width: '150px' }}
                        />
                        <Column
                            field="professionalAmount"
                            header="Honorario profesional"
                            body={(row: HonorariumPreLiquidationItemDTO) => formatMoney(row.professionalAmount)}
                            className="text-right preliq-detail__amount"
                            headerClassName="col-right"
                            style={{ width: '170px' }}
                        />
                        <Column
                            field="status"
                            header="Estado"
                            body={(row: HonorariumPreLiquidationItemDTO) => (
                                <PreLiquidationItemStatusTag statusId={row.statusId} status={row.status} />
                            )}
                            className="text-center"
                            headerClassName="col-center"
                            style={{ width: '120px' }}
                        />
                    </DataTable>
                </>
            )}
        </div>
    );
}
